import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';
import {
  useApplyDraftSessionMutation,
  useCancelAgentSessionReviewMutation,
  useDeleteDraftSessionUnitsMutation,
  useDeleteSessionMutation,
  useGetAIDraftUnitsQuery,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import formatterAIDraftUnits from 'utils/formatterAIDraftUnits';
import hasAIDraftResults from 'utils/hasAIDraftResults';

import {
  hasDraftUnitsInBoardData,
  removeDeletedDraftUnitsFromDraftStructure,
  removeDraftUnitFromBoardData,
} from '../utils/valueChainDraftUnits';

import type { IGetValueChainBoardResponse } from 'store/slices/valueChainMap/interfaces/IGetValueChainBoardResponse';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type IDraftUnitToDelete from '../interfaces/IDraftUnitToDelete';
import type {
  IUseValueChainDraftReviewParams,
  IUseValueChainDraftReviewReturn,
} from '../interfaces/IUseValueChainDraftReview';

const useValueChainDraftReview = ({
  valueChainMapId,
  boardDataResponse,
  isBoardFetching,
  nodesLength,
  nodesRef,
  setNodes,
  setEdges,
  existingSession,
  liveDraftReviewSession,
  isLiveDraftReviewSessionReady,
  draftReviewSession,
  setDraftReviewSession,
  isDraftReviewModeEnabled,
  setIsDraftReviewModeEnabled,
  getSelectedCardsFromSession,
  closeAIFlow,
  refetchValueChainMapBoard,
  onRegisterReviewGeneratedBackHandler,
  onReviewGeneratedVisibilityChange,
  setSelectedAICards,
  setGeneratingAICards,
  setBomUnitsCards,
  setBomUnitsSession,
  setSupplierUnitsCards,
  setSupplierUnitsSession,
  setSupplierUnitsInitialTabNodeId,
  setSessionId,
  setActiveAgentSession,
  setOpenAIGeneratingModal,
  setOpenAICreatorBoMUnits,
  setOpenAICreatorSupplierUnits,
  setIsAIGeneratingFloatingButtonEnabled,
  setIsBomDraftFloatingButtonVisible,
  setHasUnreadAIGeneratingResults,
  setPendingAppliedBomUnitIds,
  setExistingSessionStatusOverride,
  setIgnoredExistingSessionId,
}: IUseValueChainDraftReviewParams): IUseValueChainDraftReviewReturn => {
  const [draftData, setDraftData] = useState<IGetValueChainBoardResponse | undefined>(undefined);
  const [cachedDraftData, setCachedDraftData] = useState<IGetValueChainBoardResponse | undefined>(undefined);
  const [cachedDraftSessionId, setCachedDraftSessionId] = useState<number | undefined>(undefined);
  const [deletedDraftUnitIds, setDeletedDraftUnitIds] = useState<number[]>([]);
  const [draftUnitToDelete, setDraftUnitToDelete] = useState<IDraftUnitToDelete | undefined>(undefined);
  const [isTransitioningToDraftReview, setIsTransitioningToDraftReview] = useState(false);
  const [isBoardRefetchingAfterApply, setIsBoardRefetchingAfterApply] = useState(false);
  const [isDraftBoardBuilding, setIsDraftBoardBuilding] = useState(false);
  const boardRefetchStartedRef = useRef(false);
  const prevDraftDataRef = useRef<typeof draftData>(undefined);
  const cachedDraftDataRef = useRef<typeof cachedDraftData>(undefined);

  const [deleteDraftSessionUnits,
    { isLoading: isDeleteDraftSessionUnitsLoading }] = useDeleteDraftSessionUnitsMutation();
  const [applyDraftSession, { isLoading: isApplyDraftSessionLoading }] = useApplyDraftSessionMutation();
  const [cancelAgentSessionReview] = useCancelAgentSessionReviewMutation();
  const [deleteDraftSession, { isLoading: isDeleteDraftSessionLoading }] = useDeleteSessionMutation();

  const {
    data: draftUnits,
    isLoading: isDraftUnitsLoading,
    isSuccess: isDraftUnitsSuccess,
    isError: isDraftUnitsError,
    refetch: refetchDraftUnitsRequest,
  } = useGetAIDraftUnitsQuery({
    sessionId: draftReviewSession?.id || 0,
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !draftReviewSession?.id
      || !valueChainMapId
      || !isDraftReviewModeEnabled
      || !isLiveDraftReviewSessionReady,
  });

  const refetchDraftUnits = useCallback(() => {
    refetchDraftUnitsRequest();
  }, [refetchDraftUnitsRequest]);

  const isAIDraftUnitsReadyForSelectionSync = useMemo(
    () => !existingSession?.id || !isDraftReviewModeEnabled
      || (!isDraftUnitsLoading && (isDraftUnitsSuccess || isDraftUnitsError)),
    [existingSession?.id, isDraftReviewModeEnabled, isDraftUnitsError, isDraftUnitsLoading, isDraftUnitsSuccess],
  );

  const deletedDraftUnitIdsSet = useMemo(
    () => new Set(deletedDraftUnitIds),
    [deletedDraftUnitIds],
  );
  const visibleDraftUnitsData = useMemo(
    () => (draftUnits?.data
      ? removeDeletedDraftUnitsFromDraftStructure(draftUnits.data, deletedDraftUnitIdsSet)
      : undefined),
    [deletedDraftUnitIdsSet, draftUnits?.data],
  );
  const hasDraftResults = useMemo(
    () => hasAIDraftResults(visibleDraftUnitsData),
    [visibleDraftUnitsData],
  );
  const hasCachedDraftUnits = useMemo(
    () => hasDraftUnitsInBoardData(cachedDraftData),
    [cachedDraftData],
  );
  const hasLocalDraftUnits = useMemo(
    () => hasDraftUnitsInBoardData(draftData),
    [draftData],
  );
  const isDraftStructureLoaded = !isDraftUnitsLoading && (isDraftUnitsSuccess || isDraftUnitsError);
  const isDraftStructureEmpty = Boolean(draftUnits?.data) && !hasDraftResults && !hasCachedDraftUnits;
  const isEmptyDraftReviewMode = isDraftReviewModeEnabled
    && isLiveDraftReviewSessionReady
    && isDraftStructureLoaded
    && isDraftStructureEmpty;

  const isSupplierDiscoveryDraftReviewSession = isDraftReviewModeEnabled
    && liveDraftReviewSession?.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
    && isLiveDraftReviewSessionReady;

  const hasSupplierDiscoveryDraftUnitsFromResponse = Boolean(visibleDraftUnitsData?.draftUnits.length);
  const hasSupplierDiscoveryDraftUnits = hasSupplierDiscoveryDraftUnitsFromResponse || hasCachedDraftUnits;
  const hasReviewableDraftResults = hasDraftResults && (
    !isSupplierDiscoveryDraftReviewSession || hasSupplierDiscoveryDraftUnitsFromResponse
  );

  const isUseDraftResultDisabled = isEmptyDraftReviewMode
    || (isSupplierDiscoveryDraftReviewSession && !hasSupplierDiscoveryDraftUnits);

  useEffect(() => {
    const shouldRefetchSupplierDraftUnits = isDraftReviewModeEnabled
      && Boolean(draftReviewSession?.id)
      && liveDraftReviewSession?.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
      && isLiveDraftReviewSessionReady;

    if (!shouldRefetchSupplierDraftUnits) {
      return;
    }

    refetchDraftUnits();
  }, [
    draftReviewSession?.id,
    isDraftReviewModeEnabled,
    isLiveDraftReviewSessionReady,
    liveDraftReviewSession?.subType,
    refetchDraftUnits,
  ]);

  useEffect(() => {
    if (!isDraftReviewModeEnabled || !boardDataResponse) {
      setDraftData(undefined);
      return;
    }

    if (hasReviewableDraftResults && visibleDraftUnitsData) {
      if (deletedDraftUnitIds.length > 0) {
        return;
      }

      const mergedData = formatterAIDraftUnits(boardDataResponse, visibleDraftUnitsData);
      setDraftData(mergedData);
      setCachedDraftData(mergedData);
      cachedDraftDataRef.current = mergedData;
      setCachedDraftSessionId(draftReviewSession?.id);
      return;
    }

    if (!isDraftUnitsLoading && (isDraftUnitsSuccess || isDraftUnitsError)) {
      if (deletedDraftUnitIds.length > 0) {
        return;
      }

      setDraftData(undefined);

      if (cachedDraftSessionId === draftReviewSession?.id && !hasCachedDraftUnits) {
        setCachedDraftData(undefined);
        setCachedDraftSessionId(undefined);
      }
    }
  }, [
    boardDataResponse,
    cachedDraftSessionId,
    deletedDraftUnitIds.length,
    draftReviewSession?.id,
    hasCachedDraftUnits,
    hasLocalDraftUnits,
    hasReviewableDraftResults,
    isDraftReviewModeEnabled,
    isDraftUnitsError,
    isDraftUnitsLoading,
    isDraftUnitsSuccess,
    visibleDraftUnitsData,
  ]);

  const shouldUseCachedDraftData = isDraftReviewModeEnabled
    && Boolean(draftReviewSession?.id)
    && cachedDraftSessionId === draftReviewSession?.id;

  const boardData = isDraftReviewModeEnabled
    ? (draftData || (shouldUseCachedDraftData ? cachedDraftData : undefined) || boardDataResponse)
    : boardDataResponse;
  const isDraftReviewMode = isDraftReviewModeEnabled && Boolean(
    draftData
    || (shouldUseCachedDraftData ? cachedDraftData : undefined)
    || isSupplierDiscoveryDraftReviewSession
    || hasDraftResults
    || hasCachedDraftUnits
    || isDraftUnitsLoading
    || isEmptyDraftReviewMode,
  );
  const activeDraftSession = liveDraftReviewSession || existingSession;

  useEffect(() => {
    setDeletedDraftUnitIds([]);
  }, [draftReviewSession?.id]);

  useEffect(() => {
    cachedDraftDataRef.current = cachedDraftData;
  }, [cachedDraftData]);

  const onDeleteDraftUnit = useCallback((unitToDelete: IDraftUnitToDelete) => {
    setDraftUnitToDelete(unitToDelete);
  }, []);

  const onRejectDraftDelete = useCallback(() => {
    setDraftUnitToDelete(undefined);
  }, []);

  const onConfirmDraftDelete = useCallback(() => {
    if (!activeDraftSession?.id) {
      return;
    }
    if (!draftUnitToDelete?.id) {
      return;
    }

    const draftUnitServerId = typeof draftUnitToDelete.id === 'number'
      && draftUnitToDelete.id > 0
      ? draftUnitToDelete.id
      : undefined;
    const draftUnitRuntimeId = draftUnitToDelete.runtimeId;
    const draftUnitNodeId = draftUnitToDelete.nodeId;
    const draftUnitNodeIdsToRemove = new Set<string>();

    nodesRef.current.forEach((nodeItem) => {
      if (nodeItem.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return;
      }

      const nodeData = nodeItem.data as unknown as ICustomUnitNodeData;
      const [, rawRuntimeUnitId = ''] = nodeItem.id.split('unit_');
      const runtimeUnitId = Number(rawRuntimeUnitId);
      const isRuntimeDraftUnitMatch = Number.isInteger(runtimeUnitId) && runtimeUnitId === draftUnitRuntimeId;
      const isServerDraftUnitMatch = typeof draftUnitServerId === 'number'
        && nodeData.draftServerId === draftUnitServerId;
      const isClickedDraftNode = nodeItem.id === draftUnitNodeId;
      const isDraftUnitNode = nodeData.draft === true || typeof nodeData.draftServerId === 'number';
      const isTargetDraftNode = isClickedDraftNode
        || isRuntimeDraftUnitMatch
        || (!Number.isInteger(draftUnitRuntimeId) && isServerDraftUnitMatch);

      if (isDraftUnitNode && isTargetDraftNode) {
        draftUnitNodeIdsToRemove.add(nodeItem.id);
      }
    });

    if (draftUnitServerId) {
      setDeletedDraftUnitIds((prevIds) => (
        prevIds.includes(draftUnitServerId) ? prevIds : [...prevIds, draftUnitServerId]
      ));
    }

    const applyDraftDeleteToBoard = (
      board: IGetValueChainBoardResponse | undefined,
    ): IGetValueChainBoardResponse | undefined => (
      removeDraftUnitFromBoardData(board, draftUnitToDelete)
    );

    setCachedDraftData((prevCachedDraftData) => {
      const nextCachedDraftData = applyDraftDeleteToBoard(
        prevCachedDraftData ?? cachedDraftDataRef.current,
      );
      cachedDraftDataRef.current = nextCachedDraftData;
      return nextCachedDraftData;
    });
    setDraftData((prevDraftData) => applyDraftDeleteToBoard(
      prevDraftData ?? cachedDraftDataRef.current,
    ));

    if (draftUnitNodeIdsToRemove.size > 0) {
      setNodes((prevNodes) => prevNodes.filter((node) => !draftUnitNodeIdsToRemove.has(node.id)));
      setEdges((prevEdges) => prevEdges.filter((edge) => (
        !draftUnitNodeIdsToRemove.has(edge.source) && !draftUnitNodeIdsToRemove.has(edge.target)
      )));
    }

    setSelectedAICards((prevCards) => prevCards.filter((card) => !draftUnitNodeIdsToRemove.has(card.nodeId)));
    setGeneratingAICards((prevCards) => prevCards.filter((card) => !draftUnitNodeIdsToRemove.has(card.nodeId)));
    setDraftUnitToDelete(undefined);

    if (!draftUnitServerId) {
      return;
    }

    deleteDraftSessionUnits({
      id: draftUnitServerId,
      sessionId: activeDraftSession.id,
      valueChainMapId: valueChainMapId || '',
    })
      .unwrap()
      .catch(() => {
        setDeletedDraftUnitIds((prevIds) => prevIds.filter((id) => id !== draftUnitServerId));
        refetchDraftUnits();
      });
  }, [
    activeDraftSession?.id,
    deleteDraftSessionUnits,
    draftUnitToDelete,
    nodesRef,
    refetchDraftUnits,
    setEdges,
    setGeneratingAICards,
    setNodes,
    setSelectedAICards,
    valueChainMapId,
  ]);

  const onDiscardDraftResult = useCallback(() => {
    const draftSession = activeDraftSession;
    const mapId = valueChainMapId;

    if (!draftSession?.id || !mapId) {
      return;
    }

    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setIgnoredExistingSessionId(draftSession.id);
    setDraftUnitToDelete(undefined);
    setSelectedAICards([]);
    setGeneratingAICards([]);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setIsAIGeneratingFloatingButtonEnabled(false);
    setIsBomDraftFloatingButtonVisible(false);
    setHasUnreadAIGeneratingResults(false);

    deleteDraftSession({
      valueChainMapId: mapId,
      sessionId: draftSession.id,
    })
      .unwrap()
      .then(() => {
        refetchValueChainMapBoard();
      })
      .catch(() => {
        refetchValueChainMapBoard();
      });
  }, [
    activeDraftSession,
    deleteDraftSession,
    refetchValueChainMapBoard,
    setActiveAgentSession,
    setBomUnitsCards,
    setBomUnitsSession,
    setGeneratingAICards,
    setHasUnreadAIGeneratingResults,
    setIgnoredExistingSessionId,
    setIsAIGeneratingFloatingButtonEnabled,
    setIsBomDraftFloatingButtonVisible,
    setIsDraftReviewModeEnabled,
    setSelectedAICards,
    setSessionId,
    setSupplierUnitsCards,
    setSupplierUnitsInitialTabNodeId,
    setSupplierUnitsSession,
    setDraftReviewSession,
    valueChainMapId,
  ]);

  const onBackFromDraftReview = useCallback(() => {
    const draftSession = activeDraftSession;
    const mapId = valueChainMapId;

    if (!draftSession?.id || !mapId) {
      return;
    }

    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    setDraftData(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setIsAIGeneratingFloatingButtonEnabled(false);
    setIsBomDraftFloatingButtonVisible(false);
    setHasUnreadAIGeneratingResults(false);

    const isBillOfMaterialsReviewSession = draftSession.subType
      === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;
    const isSupplierDiscoveryReviewSession = draftSession.subType
      === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;

    if (isBillOfMaterialsReviewSession) {
      const nextBomUnitsCards = getSelectedCardsFromSession(draftSession);

      setExistingSessionStatusOverride({
        sessionId: draftSession.id,
        status: VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
      });
      cancelAgentSessionReview({
        valueChainMapId: mapId,
        sessionId: draftSession.id,
      })
        .unwrap()
        .catch(() => {})
        .finally(() => {
          refetchValueChainMapBoard();
        });
      setOpenAIGeneratingModal(false);
      setGeneratingAICards([]);
      setSelectedAICards(nextBomUnitsCards);
      setBomUnitsCards(nextBomUnitsCards);
      setBomUnitsSession({
        ...draftSession,
        status: VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
      });

      window.setTimeout(() => {
        setOpenAICreatorBoMUnits(true);
      }, VALUE_CHAIN_AI_AGENT.CARD_PANEL_SYNC_DELAY);
      return;
    }

    if (isSupplierDiscoveryReviewSession) {
      const nextSupplierUnitsCards = getSelectedCardsFromSession(draftSession);

      setExistingSessionStatusOverride({
        sessionId: draftSession.id,
        status: VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
      });
      cancelAgentSessionReview({
        valueChainMapId: mapId,
        sessionId: draftSession.id,
      })
        .unwrap()
        .catch(() => {})
        .finally(() => {
          refetchValueChainMapBoard();
        });
      setOpenAIGeneratingModal(false);
      setGeneratingAICards([]);
      setSelectedAICards(nextSupplierUnitsCards);
      setSupplierUnitsCards(nextSupplierUnitsCards);
      setSupplierUnitsSession({
        ...draftSession,
        status: VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
      });

      window.setTimeout(() => {
        setOpenAICreatorSupplierUnits(true);
      }, VALUE_CHAIN_AI_AGENT.CARD_PANEL_SYNC_DELAY);
      return;
    }

    setDraftUnitToDelete(undefined);
    setSelectedAICards([]);
    setGeneratingAICards([]);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setSessionId(undefined);
    setActiveAgentSession(undefined);

    deleteDraftSession({
      valueChainMapId: mapId,
      sessionId: draftSession.id,
    })
      .unwrap()
      .then(() => {
        refetchValueChainMapBoard();
      })
      .catch(() => {
        refetchValueChainMapBoard();
      });
  }, [
    activeDraftSession,
    cancelAgentSessionReview,
    deleteDraftSession,
    getSelectedCardsFromSession,
    refetchValueChainMapBoard,
    setActiveAgentSession,
    setBomUnitsCards,
    setBomUnitsSession,
    setExistingSessionStatusOverride,
    setGeneratingAICards,
    setHasUnreadAIGeneratingResults,
    setIsAIGeneratingFloatingButtonEnabled,
    setIsBomDraftFloatingButtonVisible,
    setIsDraftReviewModeEnabled,
    setOpenAICreatorBoMUnits,
    setOpenAICreatorSupplierUnits,
    setOpenAIGeneratingModal,
    setSelectedAICards,
    setSessionId,
    setSupplierUnitsCards,
    setSupplierUnitsInitialTabNodeId,
    setSupplierUnitsSession,
    setDraftReviewSession,
    valueChainMapId,
  ]);

  useEffect(() => {
    if (!onRegisterReviewGeneratedBackHandler) {
      return;
    }

    onRegisterReviewGeneratedBackHandler(onBackFromDraftReview);
  }, [onBackFromDraftReview, onRegisterReviewGeneratedBackHandler]);

  useEffect(() => {
    if (!onReviewGeneratedVisibilityChange) {
      return;
    }

    const nextSessionSubType = isDraftReviewMode
      ? activeDraftSession?.subType
      : undefined;

    onReviewGeneratedVisibilityChange(isDraftReviewMode, nextSessionSubType);
  }, [
    activeDraftSession?.subType,
    isDraftReviewMode,
    onReviewGeneratedVisibilityChange,
  ]);

  useEffect(() => {
    if (!isTransitioningToDraftReview) return undefined;
    if (!isDraftReviewModeEnabled) {
      setIsTransitioningToDraftReview(false);
      return undefined;
    }
    if (!isDraftReviewMode) return undefined;
    const rafId = requestAnimationFrame(() => setIsTransitioningToDraftReview(false));
    return () => cancelAnimationFrame(rafId);
  }, [boardData, isDraftReviewMode, isDraftReviewModeEnabled, isTransitioningToDraftReview]);

  useEffect(() => {
    const prevDraftData = prevDraftDataRef.current;
    prevDraftDataRef.current = draftData;

    if (!draftData) {
      return;
    }

    if (!prevDraftData || prevDraftData !== draftData) {
      setIsDraftBoardBuilding(true);
    }
  }, [draftData]);

  useEffect(() => {
    if (!isDraftBoardBuilding) {
      return undefined;
    }

    if (!draftData || boardData !== draftData) {
      return undefined;
    }

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsDraftBoardBuilding(false);
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [boardData, draftData, isDraftBoardBuilding, nodesLength]);

  useEffect(() => {
    if (!isBoardRefetchingAfterApply) {
      boardRefetchStartedRef.current = false;
      return undefined;
    }
    if (isBoardFetching) {
      boardRefetchStartedRef.current = true;
      return undefined;
    }
    if (!boardRefetchStartedRef.current) return undefined;
    const rafId = requestAnimationFrame(() => setIsBoardRefetchingAfterApply(false));
    return () => cancelAnimationFrame(rafId);
  }, [boardData, isBoardFetching, isBoardRefetchingAfterApply]);

  const onUseDraftResult = useCallback(() => {
    if (!activeDraftSession?.id || !valueChainMapId || isUseDraftResultDisabled) {
      return;
    }

    applyDraftSession({
      sessionId: activeDraftSession.id,
      valueChainMapId,
    })
      .unwrap()
      .then((response) => {
        const isBillOfMaterialsDraftSession = activeDraftSession.subType
          === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;

        setIsDraftReviewModeEnabled(false);
        setDraftReviewSession(undefined);
        setDraftData(undefined);
        setDraftUnitToDelete(undefined);
        setOpenAIGeneratingModal(false);
        setOpenAICreatorBoMUnits(false);
        setOpenAICreatorSupplierUnits(false);
        setBomUnitsCards([]);
        setBomUnitsSession(undefined);
        setSupplierUnitsCards([]);
        setSupplierUnitsSession(undefined);
        setSupplierUnitsInitialTabNodeId(undefined);
        setGeneratingAICards([]);
        setCachedDraftData(undefined);
        setCachedDraftSessionId(undefined);
        setIgnoredExistingSessionId(activeDraftSession.id);
        closeAIFlow();
        setPendingAppliedBomUnitIds([]);
        setSessionId(undefined);
        setActiveAgentSession(undefined);
        setIsAIGeneratingFloatingButtonEnabled(false);
        setIsBomDraftFloatingButtonVisible(false);
        setHasUnreadAIGeneratingResults(false);

        if (isBillOfMaterialsDraftSession && response.unitIds.length) {
          setPendingAppliedBomUnitIds(response.unitIds);
        }
        setIsBoardRefetchingAfterApply(true);
        refetchValueChainMapBoard();
      })
      .catch(() => {});
  }, [
    activeDraftSession,
    applyDraftSession,
    closeAIFlow,
    isUseDraftResultDisabled,
    refetchValueChainMapBoard,
    setActiveAgentSession,
    setBomUnitsCards,
    setBomUnitsSession,
    setGeneratingAICards,
    setHasUnreadAIGeneratingResults,
    setIgnoredExistingSessionId,
    setIsAIGeneratingFloatingButtonEnabled,
    setIsBomDraftFloatingButtonVisible,
    setIsDraftReviewModeEnabled,
    setOpenAICreatorBoMUnits,
    setOpenAICreatorSupplierUnits,
    setOpenAIGeneratingModal,
    setPendingAppliedBomUnitIds,
    setSessionId,
    setSupplierUnitsCards,
    setSupplierUnitsInitialTabNodeId,
    setSupplierUnitsSession,
    setDraftReviewSession,
    valueChainMapId,
  ]);

  return {
    boardData,
    activeDraftSession,
    draftCardToDeleteName: draftUnitToDelete?.title,
    isDraftReviewMode,
    isAIDraftUnitsReadyForSelectionSync,
    isUseDraftResultDisabled,
    isDraftDeleteModalOpen: Boolean(draftUnitToDelete),
    isDraftUnitsLoading,
    isDeleteDraftSessionUnitsLoading,
    isApplyDraftSessionLoading,
    isDeleteDraftSessionLoading,
    isTransitioningToDraftReview,
    isBoardRefetchingAfterApply,
    isDraftBoardBuilding,
    setDraftData,
    setCachedDraftData,
    setCachedDraftSessionId,
    setIsTransitioningToDraftReview,
    refetchDraftUnits,
    onDeleteDraftUnit,
    onRejectDraftDelete,
    onConfirmDraftDelete,
    onDiscardDraftResult,
    onUseDraftResult,
  };
};

export default useValueChainDraftReview;
